import { AnimatePresence, motion } from 'framer-motion';
import { Aperture, Camera, Infinity as InfinityIcon, X } from 'lucide-react';
import { useStore } from '../store/useStore';
import { STORE_BILLING_LIVE, WAITLIST_URL } from '../billing/launch';
import { publicUrl } from '../native/native';
import { storeName } from '../billing/storeLabels';
import { priceLabel } from '../billing/prices';
import { SCAN_PACK_PRICE, SCAN_PACK_SIZE } from '../types';

/** Consumable scan-token paywall (mock billing — no real charges). */
export function ScanPackModal() {
  const open = useStore((s) => s.scanPackOpen);
  const closeScanPack = useStore((s) => s.closeScanPack);
  const purchaseScanPack = useStore((s) => s.purchaseScanPack);
  const openPaywall = useStore((s) => s.openPaywall);
  const billingNotice = useStore((s) => s.billingNotice);
  const billingBusy = useStore((s) => s.billingBusy);
  const storePrices = useStore((s) => s.storePrices);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-slate-950/90 p-6 backdrop-blur-md"
          onClick={closeScanPack}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm space-y-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-2xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-black text-slate-900 dark:text-white">
                <Camera size={15} className="text-brand-600 dark:text-brand-400" />
                Camera Scan Refill
              </h3>
              <button
                onClick={closeScanPack}
                aria-label="Close"
                className="rounded-lg p-1 text-slate-500 transition-colors hover:text-slate-900 dark:hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4 text-center">
              <span className="mx-auto inline-flex rounded-3xl border border-brand-200 dark:border-brand-500/20 bg-brand-500/10 p-4 text-brand-600 dark:text-brand-400">
                <Aperture size={38} />
              </span>
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
                  Free scans used up
                </h4>
                <p className="px-2 text-xs leading-normal text-slate-500 dark:text-slate-400">
                  Grab a token pack to keep digitizing handwriting and sticky
                  notes — or go premium for unlimited scanning.
                </p>
              </div>
            </div>

            <div className="space-y-1 rounded-xl border border-brand-200 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/20 p-3 text-center">
              <p className="flex items-center justify-center gap-1 text-[11px] font-extrabold text-slate-900 dark:text-white">
                <InfinityIcon size={13} className="text-brand-600 dark:text-brand-400" />
                PREMIUM = LIMITLESS
              </p>
              <p className="text-[11px] leading-normal text-brand-700 dark:text-brand-200">
                All paid plans include unlimited scans, roadmaps, and analytics.
              </p>
            </div>

            <div className="space-y-2">
              {billingNotice && (
                <p
                  role="status"
                  className="break-words rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] leading-relaxed text-amber-800 dark:text-amber-200"
                >
                  {billingNotice}
                </p>
              )}
              {STORE_BILLING_LIVE ? (
                <button
                  onClick={() => void purchaseScanPack()}
                  disabled={billingBusy}
                  className="flex w-full items-center justify-between rounded-xl bg-brand-600 px-4 py-3.5 text-xs font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-60"
                >
                  <span>
                    {billingBusy ? `Opening ${storeName()}…` : `Buy Scan Token Pack (${SCAN_PACK_SIZE} scans)`}
                  </span>
                  <span className="rounded bg-brand-700 px-2 py-0.5 font-black">
                    {priceLabel(storePrices.scanPack, SCAN_PACK_PRICE)}
                  </span>
                </button>
              ) : (
                <a
                  href={publicUrl(WAITLIST_URL)}
                  target="_blank"
                  rel="noopener"
                  className="flex w-full items-center justify-center rounded-xl bg-brand-600 px-4 py-3.5 text-xs font-bold text-white transition-colors hover:bg-brand-500"
                >
                  Join the launch waitlist
                </a>
              )}
              <button
                onClick={() => {
                  closeScanPack();
                  openPaywall();
                }}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 py-3.5 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
              >
                Go Premium (Unlimited Scans)
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
